/**
 * OrdersSpendingReport — report spese ordini per negozio/mese con budget max.
 * Dati: GET /orders/spending-report (approvati+consegnati). Budget mensile
 * per negozio modificabile da chi ha orders.approve (celle rosse se sforato).
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { usePermissions } from '../lib/usePermissions';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ChevronLeft, ChevronRight, Loader2, PiggyBank } from 'lucide-react';
import { toast } from 'sonner';
import { fmtPrice } from '../pages/OrdersPage';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

export default function OrdersSpendingReport({ open, onClose }) {
  const { has } = usePermissions();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);
  const canEditBudget = has('orders.approve');

  const load = useCallback(async (y) => {
    setLoading(true);
    try {
      const r = await api.get('/orders/spending-report', { params: { year: y } });
      setData(r.data);
      setBudgetInput(r.data.monthly_budget != null ? String(r.data.monthly_budget) : '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(year); }, [open, year, load]);

  const saveBudget = async () => {
    const v = budgetInput.trim() === '' ? null : parseFloat(budgetInput.replace(',', '.'));
    if (v !== null && (Number.isNaN(v) || v < 0)) { toast.error('Budget non valido'); return; }
    setSavingBudget(true);
    try {
      await api.put('/orders/budget', { monthly_budget: v });
      toast.success(v === null ? 'Budget rimosso' : `Budget mensile impostato: ${fmtPrice(v)}`);
      load(year);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio budget fallito');
    } finally {
      setSavingBudget(false);
    }
  };

  const budget = data?.monthly_budget;
  const overCount = data ? data.stores.reduce((acc, s) =>
    acc + MONTHS.reduce((a, _, i) => a + (budget != null && s.monthly[String(i + 1)] > budget ? 1 : 0), 0), 0) : 0;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="spending-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PiggyBank className="w-5 h-5" /> Report spese ordini</DialogTitle>
          <DialogDescription>Totale speso per negozio e mese (ordini approvati e consegnati).</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-none" onClick={() => setYear(y => y - 1)} data-testid="spending-year-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-heading text-lg font-bold w-16 text-center" data-testid="spending-year-label">{year}</span>
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-none" onClick={() => setYear(y => y + 1)} data-testid="spending-year-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          {canEditBudget && (
            <div className="flex items-center gap-1.5 ml-auto">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Budget max mensile/negozio €</label>
              <Input value={budgetInput} onChange={e => setBudgetInput(e.target.value)} inputMode="decimal"
                     placeholder="Nessun limite" className="rounded-none h-8 w-28 text-sm" data-testid="spending-budget-input" />
              <Button size="sm" className="h-8 rounded-none" onClick={saveBudget} disabled={savingBudget} data-testid="spending-budget-save">
                {savingBudget ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Salva'}
              </Button>
            </div>
          )}
        </div>

        {budget != null && overCount > 0 && (
          <div className="text-xs font-semibold text-red-700 bg-red-50 border border-red-300 px-3 py-2" data-testid="spending-over-count">
            ⚠ {overCount} sforament{overCount === 1 ? 'o' : 'i'} del budget mensile ({fmtPrice(budget)}) nel {year}
          </div>
        )}

        {loading || !data ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : data.stores.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-12" data-testid="spending-empty">
            Nessun ordine approvato o consegnato nel {year}.
          </p>
        ) : (
          <div className="overflow-x-auto border border-slate-200">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-3 py-2 font-semibold sticky left-0 bg-slate-50">Negozio</th>
                  {MONTHS.map(m => <th key={m} className="px-2 py-2 font-semibold text-right">{m}</th>)}
                  <th className="px-3 py-2 font-semibold text-right">Totale</th>
                </tr>
              </thead>
              <tbody>
                {data.stores.map(s => (
                  <tr key={s.store_id} className="border-b border-slate-100" data-testid={`spending-row-${s.store_id}`}>
                    <td className="px-3 py-1.5 text-slate-800 font-medium whitespace-nowrap sticky left-0 bg-white">{s.store_name}</td>
                    {MONTHS.map((_, i) => {
                      const v = s.monthly[String(i + 1)] || 0;
                      const over = budget != null && v > budget;
                      return (
                        <td key={i}
                            className={`px-2 py-1.5 text-right tabular-nums ${over ? 'bg-red-100 text-red-800 font-bold' : v > 0 ? 'text-slate-700' : 'text-slate-300'}`}
                            data-testid={`spending-cell-${s.store_id}-${i + 1}`}>
                          {v > 0 ? fmtPrice(v) : '—'}
                        </td>
                      );
                    })}
                    <td className="px-3 py-1.5 text-right font-bold text-slate-900 tabular-nums">{fmtPrice(s.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-900 text-white">
                  <td className="px-3 py-2 font-semibold sticky left-0 bg-slate-900">Totale rete</td>
                  {MONTHS.map((_, i) => (
                    <td key={i} className="px-2 py-2 text-right font-semibold tabular-nums">
                      {data.monthly_totals[String(i + 1)] > 0 ? fmtPrice(data.monthly_totals[String(i + 1)]) : '—'}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-bold tabular-nums" data-testid="spending-grand-total">{fmtPrice(data.grand_total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
